'use client';

import { memo } from 'react';

import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';

import { CardVideo } from '~/components/characters/card-video';

import type { CharacterConfig, Gender, StyleType } from './types';

const PREVIEW_VIDEO_BASE =
  'https://bjhhccyzlmjpgurefeef.supabase.co/storage/v1/object/public/eros-media/create-previews';

function GenderIcon({
  gender,
  className,
}: {
  gender: string;
  className?: string;
}) {
  if (gender === 'female') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <circle cx="12" cy="9" r="6" />
        <line x1="12" y1="15" x2="12" y2="22" />
        <line x1="9" y1="19" x2="15" y2="19" />
      </svg>
    );
  }
  if (gender === 'male') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <circle cx="10" cy="14" r="6" />
        <line x1="22" y1="2" x2="15" y2="9" />
        <polyline points="16 2 22 2 22 8" />
      </svg>
    );
  }
  // trans
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="11" cy="11" r="5" />
      <line x1="11" y1="16" x2="11" y2="22" />
      <line x1="8" y1="19" x2="14" y2="19" />
      <line x1="19" y1="2" x2="13" y2="8" />
      <polyline points="14 2 19 2 19 7" />
      <line x1="19" y1="14" x2="13" y2="8" />
    </svg>
  );
}

interface Step1GenderStyleProps {
  config: CharacterConfig;
  updateConfig: (updates: Partial<CharacterConfig>) => void;
  onNext: () => void;
}

export const Step1GenderStyle = memo(function Step1GenderStyle({
  config,
  updateConfig,
  onNext,
}: Step1GenderStyleProps) {
  const { t } = useTranslation('common');

  // Get image paths based on selected gender
  const genderSlug =
    config.gender === 'female'
      ? 'woman'
      : config.gender === 'male'
        ? 'man'
        : 'trans';
  const getStyleImage = (style: 'realistic' | 'anime' | 'furry') =>
    `/images/character-creator/1st-step/${style}-${genderSlug}.avif?v=2`;
  const getStyleVideo = (style: 'realistic' | 'anime' | 'furry') =>
    `${PREVIEW_VIDEO_BASE}/${style}-${genderSlug}.mp4`;

  return (
    <div className="w-full px-2 sm:px-4 lg:px-0">
      {/* Title Section */}
      <div className="mb-2 text-center sm:mb-3">
        <h1 className="mb-0.5 text-lg font-bold text-white sm:mb-1 sm:text-xl md:text-2xl">
          <Trans i18nKey="common:characterCreator.step1Title" />
        </h1>
        <p className="bg-gradient-to-r from-red-400 via-pink-400 to-pink-600 bg-clip-text text-[10px] font-semibold text-transparent sm:text-xs md:text-sm">
          <Trans i18nKey="common:characterCreator.step1Subtitle" />
        </p>
      </div>

      {/* Gender Selection */}
      <div className="mb-3 sm:mb-4">
        <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-1.5">
          {(['female', 'male', 'trans'] as Gender[]).map((gender) => (
            <button
              key={gender}
              onClick={() => updateConfig({ gender })}
              className={`flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[9px] font-semibold transition-all duration-300 sm:gap-1 sm:px-3 sm:py-1 sm:text-[10px] md:px-4 md:text-xs ${
                config.gender === gender
                  ? 'border border-pink-500 bg-gradient-to-r from-red-600 to-pink-600 text-white shadow-md shadow-pink-500/50 sm:border-2'
                  : 'border border-gray-700 bg-transparent text-gray-400 hover:border-gray-600 sm:border-2'
              }`}
            >
              <GenderIcon
                gender={gender}
                className="h-3 w-3 sm:h-3.5 sm:w-3.5"
              />
              <span>{t(`filters.${gender}`)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Style Cards */}
      <div className="mb-3 sm:mb-4">
        <div className="mx-auto grid max-w-full grid-cols-3 gap-2 sm:max-w-md sm:gap-3 md:max-w-lg md:gap-4 lg:max-w-[1280px] lg:gap-4 xl:max-w-[1400px] xl:gap-6">
          {/* Realistic Card */}
          <button
            onClick={() => updateConfig({ style: 'realistic' })}
            className={`relative aspect-[2/5] cursor-pointer overflow-hidden rounded-xl transition-all duration-300 ease-out sm:aspect-[4/5] sm:rounded-2xl ${
              config.style === 'realistic'
                ? 'scale-[1.01] border-2 border-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.4)] sm:shadow-[0_0_15px_rgba(236,72,153,0.4)]'
                : 'border border-white/10 hover:scale-[1.01] hover:border-white/30'
            }`}
          >
            <img
              src={getStyleImage('realistic')}
              alt="Realistic style"
              className="h-full w-full object-cover"
            />
            <CardVideo
              src={getStyleVideo('realistic')}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute right-0 bottom-0 left-0 p-1.5 sm:p-2">
              <h3 className="text-xs font-bold text-white sm:text-sm md:text-base lg:text-lg">
                <Trans i18nKey="common:characterCreator.styleRealistic" />
              </h3>
            </div>
            {config.style === 'realistic' && (
              <div className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-pink-500 sm:top-1.5 sm:right-1.5 sm:h-5 sm:w-5">
                <Check className="h-2.5 w-2.5 text-white sm:h-3 sm:w-3" />
              </div>
            )}
          </button>

          {/* Anime Card */}
          <button
            onClick={() => updateConfig({ style: 'anime' })}
            className={`relative aspect-[2/5] cursor-pointer overflow-hidden rounded-xl transition-all duration-300 ease-out sm:aspect-[4/5] sm:rounded-2xl ${
              config.style === 'anime'
                ? 'scale-[1.01] border-2 border-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.4)] sm:shadow-[0_0_15px_rgba(236,72,153,0.4)]'
                : 'border border-white/10 hover:scale-[1.01] hover:border-white/30'
            }`}
          >
            <img
              src={getStyleImage('anime')}
              alt="Anime style"
              className="h-full w-full object-cover"
            />
            <CardVideo
              src={getStyleVideo('anime')}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute right-0 bottom-0 left-0 p-1.5 sm:p-2">
              <h3 className="text-xs font-bold text-white sm:text-sm md:text-base lg:text-lg">
                <Trans i18nKey="common:characterCreator.styleAnime" />
              </h3>
            </div>
            {config.style === 'anime' && (
              <div className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-pink-500 sm:top-1.5 sm:right-1.5 sm:h-5 sm:w-5">
                <Check className="h-2.5 w-2.5 text-white sm:h-3 sm:w-3" />
              </div>
            )}
          </button>

          {/* Furry Card */}
          <button
            onClick={() => updateConfig({ style: 'furry' })}
            className={`relative aspect-[2/5] cursor-pointer overflow-hidden rounded-xl transition-all duration-300 ease-out sm:aspect-[4/5] sm:rounded-2xl ${
              config.style === 'furry'
                ? 'scale-[1.01] border-2 border-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.4)] sm:shadow-[0_0_15px_rgba(236,72,153,0.4)]'
                : 'border border-white/10 hover:scale-[1.01] hover:border-white/30'
            }`}
          >
            <img
              src={getStyleImage('furry')}
              alt="Furry style"
              className="h-full w-full object-cover"
            />
            <CardVideo
              src={getStyleVideo('furry')}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute right-0 bottom-0 left-0 p-1.5 sm:p-2">
              <h3 className="text-xs font-bold text-white sm:text-sm md:text-base lg:text-lg">
                <Trans i18nKey="common:characterCreator.styleFurry" />
              </h3>
            </div>
            {config.style === 'furry' && (
              <div className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-pink-500 sm:top-1.5 sm:right-1.5 sm:h-5 sm:w-5">
                <Check className="h-2.5 w-2.5 text-white sm:h-3 sm:w-3" />
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Action Button */}
      <div className="flex justify-center">
        <button
          onClick={onNext}
          className="w-full max-w-[200px] rounded-lg bg-gradient-to-r from-red-600 via-pink-600 to-pink-700 px-6 py-2 text-[10px] font-bold text-white shadow-lg shadow-pink-500/30 transition-all hover:from-red-700 hover:via-pink-700 hover:to-pink-800 sm:w-auto sm:max-w-none sm:px-8 sm:py-2.5 sm:text-xs md:px-10 md:text-sm"
        >
          <Trans i18nKey="common:characterCreator.step1Begin" />
        </button>
      </div>
    </div>
  );
});
